// @mui
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
// form
// components
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { organizationApi } from 'src/api/organization';
import { specializationApi } from 'src/api/specialization';
import VetsiIcon from 'src/assets/icons/VetsiIcon';
import SearchResultsListCardOpeningHoursModal from 'src/sections/search/components/SearchResults/SearchResultsListCard/SearchResultsListCardOpeningHoursModal';
import { mapSpecializationNameIdToString } from 'src/utils/mapSpecializationNameIdToString';
import { useOrganizationContext } from '../../hooks/useOrganizationContext';
import { translateKey } from 'src/locales/translate';

// ----------------------------------------------------------------------

export type OrganizationDetailsInfoCardProps = {
  isLoading?: boolean;
};

export default function OrganizationDetailsInfoCard({
  isLoading,
}: OrganizationDetailsInfoCardProps) {
  const theme = useTheme();
  const { isProfessionalRoleSelected } = useOrganizationContext();
  const { organizationId } = useParams();

  const { data: organizationData, isLoading: isLoadingOrganization } = useQuery(
    ['organization', organizationId],
    () =>
      organizationApi.organizationControllerGetOrganizationById({
        id: organizationId!,
      }),
    {
      retry: 2,
      enabled: !!organizationId,
    },
  );

  const { data: specializations, isLoading: isLoadingSpecializations } =
    useQuery(
      ['specializations', organizationId],
      () => specializationApi.specializationControllerGetAllSpecializations(),
      {
        enabled: !!organizationId,
        retry: 2,
        staleTime: 5 * 60 * 1000,
        cacheTime: 5 * 60 * 1000,
      },
    );
  const organization = organizationData?.data;

  if (!organization) {
    return null;
  }

  const specializationsData = specializations?.data || [];

  return (
    <Card
      sx={{
        pt: 10,
        pb: 3,
        display: 'flex',
        flex: 1,
        boxShadow: theme.customShadows.z24,
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Stack
        sx={{
          gap: 5,
          px: 3,
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              width: 144,
              height: 144,
              display: 'flex',
              cursor: 'pointer',
              overflow: 'hidden',
              borderRadius: '50%',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              border: `1px dashed ${alpha(theme.palette.grey[500], 0.32)}`,
            }}
          >
            <Box
              sx={{
                zIndex: 7,
                display: 'flex',
                borderRadius: '50%',
                position: 'absolute',
                alignItems: 'center',
                justifyContent: 'center',
                width: 128,
                height: 128,
                color: theme.palette.text.disabled,
                backgroundColor: theme.palette.primary.lighter,
                transition: theme.transitions.create('opacity', {
                  easing: theme.transitions.easing.easeInOut,
                  duration: theme.transitions.duration.shorter,
                }),
              }}
            >
              <CircularProgress />
            </Box>
          </Box>
        ) : (
          <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
            <Avatar
              src={organization.logoUrl}
              sx={{ width: 144, height: 144 }}
            />
          </Box>
        )}
        <Typography variant="h5">{organization.name}</Typography>
        <Chip
          label={organization.isActive ? 'Aktywna' : 'Nieaktywna'}
          variant="soft"
          color={organization.isActive ? 'success' : 'error'}
        />
      </Stack>
      <Divider sx={{ borderStyle: 'dashed', width: '100%' }} />
      <Stack sx={{ gap: 2, flex: 1, px: 3 }}>
        {organization.nip && (
          <Stack
            sx={{
              gap: 2,
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <VetsiIcon />
            <Typography variant="body2">
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
              >
                NIP:{' '}
              </Typography>
              {organization.nip}
            </Typography>
          </Stack>
        )}
        <Stack
          sx={{
            gap: 2,
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <VetsiIcon />
          <Typography variant="body2">
            <Typography component="span" variant="body2" color="text.secondary">{translateKey("search.book.citySummary")}{' '}
            </Typography>
            {organization.city}
          </Typography>
        </Stack>
        <Stack
          sx={{
            gap: 2,
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <VetsiIcon />
          <Typography variant="body2">
            <Typography component="span" variant="body2" color="text.secondary">
              Adres:{' '}
            </Typography>
            {organization.street}
          </Typography>
        </Stack>
        <Stack
          sx={{
            gap: 2,
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <VetsiIcon />
          <Typography variant="body2">
            <Typography component="span" variant="body2" color="text.secondary">
              Kod pocztowy:{' '}
            </Typography>
            {organization.zip}
          </Typography>
        </Stack>
        {isProfessionalRoleSelected && (
          <>
            <Stack
              sx={{
                gap: 2,
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <VetsiIcon />
              <Typography variant="body2">
                <SearchResultsListCardOpeningHoursModal
                  organization={{
                    ...organization,
                    id: organization._id,
                    workingHours: organization.workingHours as any,
                    timeOff: organization.timeOff as any,
                    specializations: organization.specializations as any,
                    enabledVisitTypes: organization.enabledVisitTypes || [],
                    enabledCategories: organization.enabledCategories || [],
                  }}
                />
              </Typography>
            </Stack>
            <Stack
              sx={{
                gap: 2,
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <VetsiIcon />
              <Typography variant="body2">
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >{translateKey("quickBooking.phoneLabel")}{' '}
                </Typography>
                {organization.phoneNumber}
              </Typography>
            </Stack>
            {organization.website && (
              <Stack
                sx={{
                  gap: 2,
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <VetsiIcon />
                <Typography variant="body2">
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    Strona:{' '}
                  </Typography>
                  <Link
                    href={
                      organization.website.startsWith('http')
                        ? organization.website
                        : `https://${organization.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{
                      textDecoration: 'underline',
                      color: 'text.primary',
                    }}
                  >
                    {organization.website}
                  </Link>
                </Typography>
              </Stack>
            )}
          </>
        )}
      </Stack>
      {isProfessionalRoleSelected && (
        <>
          <Divider sx={{ borderStyle: 'dashed', width: '100%' }} />
          <Stack sx={{ gap: 2, px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Specjalizacje
            </Typography>
            <Stack sx={{ gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}>
              {isLoadingSpecializations || isLoadingOrganization
                ? // Skeleton chips with different widths
                  [60, 80, 70, 90, 65, 85, 75, 95, 55, 100].map(
                    (width, index) => (
                      <Skeleton
                        key={index}
                        variant="rounded"
                        width={width}
                        height={24}
                        sx={{ borderRadius: '12px' }}
                      />
                    ),
                  )
                : organization.specializations?.map((specId, index) => {
                    const specialization = specializationsData.find(
                      (s) => s.id === specId,
                    );
                    return specialization ? (
                      <Chip
                        key={index}
                        label={mapSpecializationNameIdToString(specialization)}
                        size="small"
                        variant="outlined"
                      />
                    ) : null;
                  })}
            </Stack>
          </Stack>
        </>
      )}
    </Card>
  );
}
